import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Sentry, setSentryUser } from "@/lib/sentry";

// Records legal acceptances for a freshly-confirmed user. Called from the
// onAuthStateChange listener when `pending_legal_acceptance` is true on the
// user's metadata. Runs only after email confirmation, when the user has a
// real session token - required by the record_legal_acceptances edge
// function's JWT check. On success, clears the flag. On failure, leaves the
// flag set so we retry on the next SIGNED_IN.
const recordPendingLegalAcceptance = async (user: User) => {
  try {
    const { data: docs, error: docsError } = await supabase
      .from("legal_documents")
      .select("id")
      .eq("is_current", true);
    if (docsError) throw docsError;
    if (!docs || docs.length === 0) {
      throw new Error("No current legal documents configured");
    }

    const { error: invokeError } = await supabase.functions.invoke(
      "record_legal_acceptances",
      {
        body: {
          acceptance_context: "signup",
          document_ids: docs.map((d) => d.id),
        },
      },
    );
    if (invokeError) throw invokeError;

    // Clear the flag so we don't try again on every future SIGNED_IN.
    const { error: updateError } = await supabase.auth.updateUser({
      data: { pending_legal_acceptance: false },
    });
    if (updateError) throw updateError;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "legal_acceptance_post_confirm" },
      extra: { user_id: user.id },
    });
    // Do NOT clear the flag on failure - we want to retry on the next
    // SIGNED_IN. Show a non-blocking toast so the user is aware but isn't
    // gated by it (admin approval flow still applies).
    toast.error(
      "We could not record your acceptances. Please contact privacy@fbn-i.org if this persists.",
    );
  }
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Listener for ONGOING auth changes (does NOT control loading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setSentryUser(session?.user?.id ?? null);

        // Track login timestamp
        if (event === "SIGNED_IN" && session?.user) {
          supabase
            .from("profiles")
            .update({ last_login_at: new Date().toISOString() })
            .eq("id", session.user.id)
            .then();
        }

        // Record legal acceptances on the first SIGNED_IN AFTER email
        // confirmation. We can't do this at auth.signUp time because email
        // confirmation is required and signUp does not issue a session in
        // that case. The user picks up the flag on first real login
        // post-confirm. Helper clears the flag on success and leaves it
        // set on failure so we retry on the next SIGNED_IN.
        if (
          event === "SIGNED_IN" &&
          session?.user?.user_metadata?.pending_legal_acceptance === true
        ) {
          void recordPendingLegalAcceptance(session.user);
        }
      }
    );

    // INITIAL load (controls loading state)
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setSentryUser(session?.user?.id ?? null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Full reload to "/" - clears all React Query cache, all React state,
    // and avoids the ProtectedRoute → Navigate → history.replaceState loop
    // that fires when supabase emits multiple auth events during signout.
    window.location.assign("/");
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
